import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminModal, BTN_BLACK, BTN_WHITE, INPUT, errText } from "./AdminModal";

const INHERIT_LABEL = { default: "Built-in", style: "Same as all forms", page: "Same as form default" };

function RuleRow({ f, value, scope, onChange }) {
  const inherited = f.inherited_required ? "required" : "optional";
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-zinc-200 py-2 text-sm" data-testid={`form-rule-row-${f.key}`}>
      <span>{f.label}</span>
      <select className={`${INPUT} mt-0 w-56`} value={value === undefined ? "" : value ? "required" : "optional"}
              onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value === "required")}
              data-testid={`form-rule-select-${f.key}`}>
        <option value="">{INHERIT_LABEL[scope]} ({inherited})</option>
        <option value="required">Required</option>
        <option value="optional">Optional</option>
      </select>
    </div>
  );
}

function TargetPicker({ target, setTarget }) {
  const [styles, setStyles] = useState([]);
  useEffect(() => {
    api.get("/admin/lead-fields/catalog").then((r) => setStyles(r.data.form_styles || [])).catch(() => {});
  }, []);
  return (
    <label className="label-mono mb-3 block">Apply to
      <select className={INPUT} value={target} onChange={(e) => setTarget(e.target.value)} data-testid="form-rules-target">
        <option value="default:">All forms (the default)</option>
        {styles.map((s) => <option key={s.key} value={`style:${s.key}`}>Only the {s.label.toLowerCase()}</option>)}
      </select>
    </label>
  );
}

// scope "page" = one funnel or buyer; otherwise the admin picks all forms or one form style.
export function FormRulesDialog({ scope: pageScope, scopeKey = "", title, onClose }) {
  const [target, setTarget] = useState(pageScope === "page" ? `page:${scopeKey}` : "default:");
  const [data, setData] = useState(null);
  const [rules, setRules] = useState({});
  const [saving, setSaving] = useState(false);
  const [scope, key] = [target.slice(0, target.indexOf(":")), target.slice(target.indexOf(":") + 1)];

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get("/admin/form-rules", { params: { scope, key } })
      .then((r) => { if (alive) { setData(r.data); setRules(r.data.overrides || {}); } })
      .catch((e) => { if (alive) toast.error(errText(e, "Couldn't load form questions")); });
    return () => { alive = false; };
  }, [scope, key]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/form-rules", { scope, key, rules });
      setData(r.data);
      toast.success("Form questions saved");
    } catch (e) {
      toast.error(errText(e, "Couldn't save form questions"));
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button type="button" className={BTN_WHITE} onClick={onClose} data-testid="form-rules-cancel">Close</button>
      <button type="button" className={BTN_BLACK} onClick={save} disabled={!data || saving} data-testid="form-rules-save">
        {saving ? "Saving…" : "Save questions"}
      </button>
    </>
  );
  return (
    <AdminModal eyebrow="Form questions" title={title || "Required and optional questions"} onClose={onClose} footer={footer} testId="form-rules-modal">
      {pageScope !== "page" && <TargetPicker target={target} setTarget={setTarget} />}
      <p className="mb-2 text-[11px] text-zinc-600">
        Pick which questions visitors must answer. Optional questions get a Skip button. Leads that skip a
        qualifying question (hurt, treatment, lawyer) may not qualify for buyers.
      </p>
      {!data ? <div className="label-mono py-4">Loading…</div> : (
        <>
          {data.fields.map((f) => (
            <RuleRow key={f.key} f={f} scope={scope} value={rules[f.key]}
                     onChange={(v) => setRules((r) => {
                       const n = { ...r };
                       if (v === undefined) delete n[f.key]; else n[f.key] = v;
                       return n;
                     })} />
          ))}
          <div className="mt-3 text-[11px] text-zinc-500" data-testid="form-rules-locked">
            Always required: {data.locked.map((l) => l.label).join(", ")}.
          </div>
        </>
      )}
    </AdminModal>
  );
}
